'use client';

import React, { useState, useCallback } from 'react';
import { Search, Filter, Pencil, Trash2, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Copy, CheckCheck, Eye } from 'lucide-react';
import type { SurveyRecord } from '@/types/survey';
import type { SurveyStatus } from '@/components/ui/StatusBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import { TableRowSkeleton } from '@/components/ui/LoadingSkeleton';
import ConfirmModal from '@/components/ui/ConfirmModal';
import EditRecordModal from '@/components/ui/EditRecordModal';
import { toast } from 'sonner';

interface SurveyTableClientProps {
  records: SurveyRecord[];
  isAdmin: boolean;
  isLoading: boolean;
  onDelete: (id: string) => void;
  onEdit: (updated: SurveyRecord) => void;
}

type SortField = 'pid' | 'username' | 'status' | 'ip' | 'timestamp';
type SortDir = 'asc' | 'desc';

const STATUS_FILTER_OPTIONS: { value: SurveyStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'complete', label: 'Complete' },
  { value: 'terminate', label: 'Terminate' },
  { value: 'qualitySecurity', label: 'Quality / Security' },
  { value: 'quotaFull', label: 'Quota Full' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function SurveyTableClient({
  records,
  isAdmin,
  isLoading,
  onDelete,
  onEdit,
}: SurveyTableClientProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<SurveyRecord | null>(null);
  const [editTarget, setEditTarget] = useState<SurveyRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={13} className="text-muted-foreground/40" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-primary" />
      : <ChevronDown size={13} className="text-primary" />;
  };

  const filtered = records
    .filter((r) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        r.pid.toLowerCase().includes(q) ||
        r.username.toLowerCase().includes(q) ||
        r.ip.includes(q) ||
        r.status.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleCopyIp = useCallback((ip: string, recordId: string) => {
    navigator.clipboard.writeText(ip).then(() => {
      setCopiedId(recordId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    onDelete(deleteTarget.id);
    toast.success(`Record ${deleteTarget.pid} deleted`);
    setDeleteTarget(null);
  };

  const handleEditSave = async (updated: SurveyRecord) => {
    setIsSaving(true);
    await onEdit(updated);
    toast.success('Record updated successfully');
    setIsSaving(false);
    setEditTarget(null);
  };

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === paginated.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginated.map((r) => r.id)));
    }
  };

  const handleBulkDelete = () => {
    selectedRows.forEach((id) => onDelete(id));
    toast.success(`${selectedRows.size} records deleted`);
    setSelectedRows(new Set());
    setBulkDeleteOpen(false);
  };

  const pageNumbers: number[] = [];
  const startPage = Math.max(1, page - 2);
  const endPage = Math.min(totalPages, page + 2);
  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

  return (
    <>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-0 w-full sm:w-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search PID, username, IP…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition-colors"
          />
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-muted-foreground flex-shrink-0" />
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <button
                key={`filter-${opt.value}`}
                onClick={() => { setStatusFilter(opt.value); setPage(1); }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                  statusFilter === opt.value
                    ? 'bg-primary/20 text-primary border-primary/40' :'bg-transparent text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk delete — admin only */}
        {isAdmin && selectedRows.size > 0 && (
          <button
            onClick={() => setBulkDeleteOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30 transition-all duration-150 active:scale-95 slide-up"
          >
            <Trash2 size={13} />
            Delete {selectedRows.size} selected
          </button>
        )}
      </div>

      {/* Table */}
      <div className="glass-table rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border/60">
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="rounded border-border bg-input accent-primary cursor-pointer"
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {[
                  { field: 'pid' as SortField, label: 'PID' },
                  { field: 'username' as SortField, label: 'Username (UID)' },
                  { field: 'status' as SortField, label: 'Status' },
                  { field: 'ip' as SortField, label: 'IP Address' },
                  { field: 'timestamp' as SortField, label: 'Timestamp' },
                ].map((col) => (
                  <th
                    key={`th-${col.field}`}
                    className="px-4 py-3 text-left"
                  >
                    <button
                      onClick={() => handleSort(col.field)}
                      className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground uppercase tracking-wide transition-colors"
                    >
                      {col.label}
                      <SortIcon field={col.field} />
                    </button>
                  </th>
                ))}
                {isAdmin && (
                  <th className="px-4 py-3 text-right">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</span>
                  </th>
                )}
                {!isAdmin && (
                  <th className="px-4 py-3 text-right">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">View</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRowSkeleton key={`skel-row-${i}`} cols={isAdmin ? 7 : 6} />
                ))
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-muted border border-border flex items-center justify-center">
                        <Search size={20} className="text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">No survey records found</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {search || statusFilter !== 'all' ?'Try adjusting your search or filter criteria' :'Records appear here when survey redirects are captured via URL parameters'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((record) => (
                  <tr
                    key={record.id}
                    className={`border-b border-border/40 transition-colors duration-150 hover:bg-white/[0.025] group ${
                      record.isNew ? 'highlight-pulse' : ''
                    } ${record.isExiting ? 'row-exit' : ''}`}
                  >
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(record.id)}
                          onChange={() => toggleRow(record.id)}
                          className="rounded border-border bg-input accent-primary cursor-pointer"
                          aria-label={`Select record ${record.pid}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono font-medium text-foreground">{record.pid}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{record.username}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={record.status} size="sm" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-muted-foreground tabular-nums">
                          {record.ip}
                        </span>
                        <button
                          onClick={() => handleCopyIp(record.ip, record.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground transition-all duration-150"
                          title="Copy IP address"
                          aria-label={`Copy IP ${record.ip}`}
                        >
                          {copiedId === record.id ? (
                            <CheckCheck size={12} className="text-green-400" />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {record.timestamp}
                      </span>
                    </td>
                    {isAdmin ? (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          <button
                            onClick={() => setEditTarget(record)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150"
                            title="Edit this record"
                            aria-label={`Edit record ${record.pid}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(record)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                            title="Delete this record — this cannot be undone"
                            aria-label={`Delete record ${record.pid}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    ) : (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          <button
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
                            title="View record details"
                            aria-label={`View record ${record.pid}`}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border/60">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Showing</span>
              <span className="font-medium text-foreground tabular-nums">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)}
              </span>
              <span>of</span>
              <span className="font-medium text-foreground tabular-nums">{filtered.length}</span>
              <span>records</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="ml-2 px-2 py-1 rounded bg-input border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring/40"
                aria-label="Records per page"
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={`ps-${s}`} value={s}>{s} / page</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                aria-label="Previous page"
              >
                <ChevronLeft size={15} />
              </button>

              {pageNumbers.map((n) => (
                <button
                  key={`page-${n}`}
                  onClick={() => setPage(n)}
                  className={`w-7 h-7 rounded-lg text-xs font-medium transition-all duration-150 ${
                    page === n
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                  aria-label={`Go to page ${n}`}
                  aria-current={page === n ? 'page' : undefined}
                >
                  {n}
                </button>
              ))}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                aria-label="Next page"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete single confirm */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Survey Record"
        description={`This will permanently remove the record for PID ${deleteTarget?.pid} (${deleteTarget?.username}). This action cannot be undone.`}
        confirmLabel="Delete Record"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        dangerous
      />

      {/* Bulk delete confirm */}
      <ConfirmModal
        isOpen={bulkDeleteOpen}
        title={`Delete ${selectedRows.size} Records`}
        description={`This will permanently remove ${selectedRows.size} selected survey records. This action cannot be undone.`}
        confirmLabel={`Delete ${selectedRows.size} Records`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
        dangerous
      />

      {/* Edit modal */}
      <EditRecordModal
        isOpen={!!editTarget}
        record={editTarget}
        onSave={handleEditSave}
        onClose={() => setEditTarget(null)}
        isSaving={isSaving}
      />
    </>
  );
}